/**
 * useProjectThemes - Hook for managing V2 themes attached to a project.
 *
 * Loads themes from projects/{projectId}/projectThemes/ subcollection.
 * Provides CRUD operations and active theme management.
 *
 * @module themes/hooks
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ProjectThemeService } from '@modules/firebase-core/services/ProjectThemeService';
import type { ProjectThemeV2 } from '@modules/firebase-core/services/ProjectThemeService';
import type { CreateThemeV2Input, ThemeV2 } from '../types/themesV2.types';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

export interface UseProjectThemesReturn {
  /** All project themes */
  themes: ProjectThemeV2[];
  /** Currently active theme (null if none) */
  activeTheme: ProjectThemeV2 | null;
  /** Loading state */
  loading: boolean;
  /** Error if any operation failed */
  error: Error | null;
  /** Add a global theme to the project (clones and sets as active) */
  addTheme: (theme: ThemeV2) => Promise<ProjectThemeV2>;
  /** Create a non-active generated candidate in project themes */
  createCandidate: (theme: CreateThemeV2Input) => Promise<ProjectThemeV2>;
  /** Remove a project theme */
  removeTheme: (themeId: string) => Promise<void>;
  /** Set a theme as active */
  setActive: (themeId: string) => Promise<void>;
  /** Update a project theme */
  updateTheme: (themeId: string, data: Partial<OnboardingThemeSettingsV2>) => Promise<void>;
  /** Refresh theme list */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing project-level V2 themes.
 *
 * @param projectId - Project to manage themes for
 */
export function useProjectThemes(projectId: string): UseProjectThemesReturn {
  const [themes, setThemes] = useState<ProjectThemeV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const serviceRef = useRef(new ProjectThemeService());

  const load = useCallback(async () => {
    if (!projectId) {
      setThemes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await serviceRef.current.list(projectId);
      setThemes(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load project themes'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeTheme = themes.find((t) => t.isActive) ?? null;

  const addTheme = useCallback(
    async (theme: ThemeV2): Promise<ProjectThemeV2> => {
      const themeData: { id: string; name: string } & OnboardingThemeSettingsV2 = {
        id: theme.id,
        name: theme.name,
        colors: theme.colors,
        fonts: theme.fonts,
        optionsListSettings: theme.optionsListSettings,
        layoutSettings: theme.layoutSettings,
        background: theme.background,
        headerBar: theme.headerBar,
        actionBar: theme.actionBar,
        sourceThemeId: theme.id,
        flowDocument: theme.flowDocument,
        generatedMetadata: theme.generatedMetadata,
      };

      const created = await serviceRef.current.add(projectId, themeData);
      await load(); // Refresh list
      return created;
    },
    [projectId, load]
  );

  const createCandidate = useCallback(
    async (theme: CreateThemeV2Input): Promise<ProjectThemeV2> => {
      if (!theme.name || !theme.colors || !theme.fonts || !theme.optionsListSettings || !theme.background || !theme.headerBar || !theme.actionBar) {
        throw new Error('Generated theme candidate is missing required Theme V2 fields');
      }

      const themeData: { name: string } & OnboardingThemeSettingsV2 = {
        name: theme.name,
        colors: theme.colors,
        fonts: theme.fonts,
        optionsListSettings: theme.optionsListSettings,
        layoutSettings: theme.layoutSettings,
        background: theme.background,
        headerBar: theme.headerBar,
        actionBar: theme.actionBar,
        flowDocument: theme.flowDocument,
        generatedMetadata: theme.generatedMetadata,
      };

      const created = await serviceRef.current.createCandidate(projectId, themeData);
      await load();
      return created;
    },
    [projectId, load]
  );

  const removeTheme = useCallback(
    async (themeId: string) => {
      await serviceRef.current.delete(projectId, themeId);
      await load();
    },
    [projectId, load]
  );

  const setActive = useCallback(
    async (themeId: string) => {
      await serviceRef.current.setActive(projectId, themeId);
      await load();
    },
    [projectId, load]
  );

  const updateTheme = useCallback(
    async (themeId: string, data: Partial<OnboardingThemeSettingsV2>) => {
      await serviceRef.current.update(projectId, themeId, data);
      await load();
    },
    [projectId, load]
  );

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  return {
    themes,
    activeTheme,
    loading,
    error,
    addTheme,
    createCandidate,
    removeTheme,
    setActive,
    updateTheme,
    refresh,
  };
}
